<template>
  <div class="login-page">
    <!-- Left side: form -->
    <div class="login-left">
      <div class="login-card">
        <h2 class="title">Forgot Password</h2>
        <p class="subtitle">Enter your email to receive a reset link</p>

        <form @submit.prevent="sendResetEmail" class="form-login">
          <div class="input-group">
            <input
              type="email"
              v-model="email"
              placeholder="Email Address"
              :class="{ 'is-invalid': emailError }"
            />
            <div class="invalid-feedback">{{ emailError }}</div>
          </div>

          <button type="submit" class="btn-login" :disabled="loading">
            <span v-if="loading" class="spinner"></span>
            <span v-if="!loading">Send Reset Link</span>
          </button>

          <div v-if="message" :class="['alert', alertType]">
            {{ message }}
          </div>

          <div class="extra-links">
            <router-link to="/login">Back to Login</router-link> |
            <router-link to="/register">Create Account</router-link>
          </div>
        </form>
      </div>
    </div>

    <!-- Right side: image background -->
    <div class="login-bg"></div>
  </div>
</template>

<script>
import { auth } from "@/firebase";
import { sendPasswordResetEmail } from "firebase/auth";

export default {
  name: "ForgotPassword",
  data() {
    return {
      email: "",
      loading: false,
      message: "",
      alertType: "",
      emailError: "",
    };
  },
  methods: {
    validateEmail() {
      if (!this.email) {
        this.emailError = "Email is required.";
        return false;
      }
      const pattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
      if (!pattern.test(this.email)) {
        this.emailError = "Please enter a valid email address.";
        return false;
      }
      this.emailError = "";
      return true;
    },

    async sendResetEmail() {
      if (!this.validateEmail()) return;
      this.loading = true;
      this.message = "";
      this.alertType = "";

      try {
        await sendPasswordResetEmail(auth, this.email);
        this.message = "✅ A password reset link has been sent to your email.";
        this.alertType = "alert-success";
      } catch (error) {
        console.error(error);
        if (error.code === "auth/user-not-found") {
          this.message = "No account found with this email address.";
        } else {
          this.message = "Failed to send reset email. Please try again.";
        }
        this.alertType = "alert-danger";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-page {
  display: flex;
  min-height: 100vh;
  font-family: 'Poppins', sans-serif;
}

.login-left {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #2c787e;
}

.login-bg {
  width: 50%;
  background-image: url("@/assets/images/login.jpeg"); /* same as login page */
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
}

/* Card styling */
.login-card {
  background: #fff;
  padding: 40px 30px;
  border-radius: 15px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
  width: 380px;
  text-align: center;
}

.title {
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}

.subtitle {
  font-size: 14px;
  margin-bottom: 30px;
  color: #666;
}

/* Input */
.input-group {
  margin-bottom: 20px;
  position: relative;
}

input {
  width: 100%;
  padding: 12px 15px;
  border-radius: 10px;
  border: 1px solid #ccc;
  outline: none;
  transition: 0.3s;
}

input:focus {
  border-color: #6b73ff;
  box-shadow: 0 0 5px rgba(107, 115, 255, 0.5);
}

.is-invalid {
  border-color: #ff4d4f;
}

.invalid-feedback {
  font-size: 12px;
  color: #ff4d4f;
  text-align: left;
  margin-top: 5px;
}

/* Buttons */
.btn-login {
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  background: #2c787e;
  color: #fff;
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: 0.3s;
}

.btn-login:hover {
  background: #2c787e;
}

/* Spinner */
.spinner {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #fff;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: inline-block;
  animation: spin 0.8s linear infinite;
  margin-right: 5px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Alerts */
.alert {
  margin-top: 20px;
  padding: 10px;
  border-radius: 8px;
  font-size: 13px;
  text-align: center;
}

.alert-success {
  background-color: #e9f7ef;
  color: #28a745;
}

.alert-danger {
  background-color: #fdecea;
  color: #dc3545;
}

/* Extra links */
.extra-links {
  margin-top: 20px;
  font-size: 13px;
  color: #666;
}

.extra-links a {
  color: #2c787e;
  text-decoration: none;
}

.extra-links a:hover {
  text-decoration: underline;
}

@media (max-width: 992px) {
  .login-page {
    flex-direction: column;
  }
  .login-left,
  .login-right {
    flex: unset;
    width: 100%;
  }
  .login-right {
    padding: 20px;
  }
}
</style>
